import React, { useState, useEffect  } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Image } from "primereact/image";
import BottomMessagePopUp from "../../../components/BottomMessagePopUp";
import FloatLabelInput from "../../../components/FloatingLabel";
import FloatingSelect from "../../../components/FloatingSelect";
import FloatDateInput from "../../../components/FloatDateInput";
import lightbulb from "../../../assets/images/lightbulb.png";
import { updateHealthEntry } from "../../../utils/backend";
import useGetOrderDetailsId from "../../../store/selectors/get-order-details";
import useGetToken from "../../../store/selectors/get-token";
import "../../../assets/styles/nominate-add-button.css";
import { useTranslation } from 'react-i18next';

const Nomination = () => {
	const { t } = useTranslation();
	// Initialize with one default nominee
	const [loading, setLoading] = useState(false);
	const [nominees, setNominees] = useState([
		{
			seq_no: 1,
			nominee_name: "",
			nominee_ic: "",
			nominee_dob: new Date(),
			nominee_age: "",
			relationship: "",
			nominee_share: "",
		},
	]);

	const getOrderDetails = useGetOrderDetailsId();
	const token = useGetToken();

	const maxNominees = 3; // Maximum allowed nominees
	const navigate = useNavigate();
	const [errorMessage, setErrorMessage] = useState("");
	const [isErrorVisible, setIsErrorVisible] = useState(false);
	const showErrorMessage = (msg) => {
		setErrorMessage(msg);
		setIsErrorVisible(true);
	};

	const updateCache = (key, value) => {
		localStorage.setItem(key, JSON.stringify(value));
	};

	const relationOptions = [
		// { value: "", label: "Please select relationship" },
		{ value: "sibling", label: `${t("healthInsurance.nomination.relationships.sibling")}` },
		{ value: "mother", label: `${t("healthInsurance.nomination.relationships.mother")}` },
		{ value: "father", label: `${t("healthInsurance.nomination.relationships.father")}` },
		{ value: "partner", label: `${t("healthInsurance.nomination.relationships.partner")}` },
		{ value: "child", label: `${t("healthInsurance.nomination.relationships.child")}` },
	];

	const shareOptions = [
		// { value: "", label: "Please select share" },
		{ value: "10", label: "10%" },
		{ value: "20", label: "20%" },
		{ value: "30", label: "30%" },
		{ value: "40", label: "40%" },
		{ value: "50", label: "50%" },
		{ value: "60", label: "60%" },
		{ value: "70", label: "70%" },
		{ value: "80", label: "80%" },
		{ value: "90", label: "90%" },
		{ value: "100", label: "100%" },
	];

	useEffect(() => {
        const cachedNominees = localStorage.getItem("nominees");
        if (cachedNominees) {
            setNominees(JSON.parse(cachedNominees));
        }
    }, []);

	// Handle adding a new nominee
	const addNominee = () => {
		if (nominees.length < maxNominees) {
			const updatedNominees = [
				...nominees,
				{
					seq_no: nominees.length + 1,
					nominee_name: "",
					nominee_ic: "",
					nominee_dob: new Date(),
					nominee_age: "",
					relationship: "",
					nominee_share: "",
				},
			];
			setNominees(updatedNominees);
			updateCache("nominees", updatedNominees);
		} else {
			showErrorMessage(`${t("messages.maxNominees")}`);
		}
	};

	// Handle removing a nominee
	const removeNominee = (index) => {
		const updatedNominees = nominees.filter((_, i) => i !== index);
		const updatedWithSeqNo = updatedNominees.map((nominee, idx) => ({
			...nominee,
			seq_no: idx + 1,
		}));
		setNominees(updatedWithSeqNo);
		updateCache("nominees", updatedWithSeqNo);
	};

	// Handle nominee input change
	const handleInputChange = (index, field, value) => {
		const updatedNominees = [...nominees];
	
		if (field === "nominee_dob" && value) {
			const date = new Date(value); // Ensure it's a valid Date object
			// updatedNominees[index][field] = new Date(date.getFullYear(), date.getMonth(), date.getDate());
			updatedNominees[index][field] = date;

			// Calculate age
			const today = new Date();
			let age = today.getFullYear() - date.getFullYear();
			const monthDiff = today.getMonth() - date.getMonth();

			if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
				age--;
			}

			updatedNominees[index]["nominee_age"] = age;
		} else if (field === "nominee_ic") {
			const numbersOnly = value.replace(/[^\d]/g, '');

			if (numbersOnly.length <= 12) {
				updatedNominees[index][field] = numbersOnly;
			}

			// calculate dob and age
			if (numbersOnly.length === 12) {
				const year = parseInt(numbersOnly.slice(0, 2), 10);
				const month = parseInt(numbersOnly.slice(2, 4), 10) - 1; // Months are 0-based
				const day = parseInt(numbersOnly.slice(4, 6), 10);

				// Determine century (if year >= 50, assume 1900s, else 2000s)
				const fullYear = year >= 50 ? 1900 + year : 2000 + year;

				// Set DOB
				const birthDate = new Date(fullYear, month, day);
				updatedNominees[index]["nominee_dob"] = birthDate;

				// Calculate age
				const today = new Date();
				let age = today.getFullYear() - fullYear;
				const monthDiff = today.getMonth() - month;

				if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
					age--;
				}

				updatedNominees[index]["nominee_age"] = age;
			}
		} else if (field === "nominee_share") {
			const oldValue = nominees[index].nominee_share ? parseInt(nominees[index].nominee_share, 10) : 0;
			const newShare = parseInt(value, 10);
			const totalShare = nominees.reduce((acc, curr) => acc + (curr.nominee_share ? parseInt(curr.nominee_share, 10) : 0), 0) - oldValue;

			if (totalShare + newShare > 100) {
				showErrorMessage(`${t("messages.totalShareExceeds")}`);
				return;
			}

			updatedNominees[index][field] = value;
		} else {
			updatedNominees[index][field] = value;
		}
	
		setNominees(updatedNominees);
		updateCache("nominees", updatedNominees);
	};

	// Proceed button click
	const handleButtonClick = async (e) => {
		e.preventDefault();

		if (nominees.length === 0) {
			navigate(`/get-health-quotes/declaration-two`);
		}

		if (nominees.length > 0) {
			const validNominees = nominees.filter(nominee => nominee.nominee_name || nominee.nominee_ic || nominee.nominee_share || nominee.nominee_age || nominee.relationship);

			if (validNominees.some(nominee => !nominee.nominee_name || nominee.nominee_name.trim() === '')) {
				showErrorMessage(`${t("messages.nomineeNameValidation")}`);
				return;
			}

			if (validNominees.some(nominee => !nominee.nominee_ic || nominee.nominee_ic.trim() === '')) {
				showErrorMessage(`${t("messages.nomineeIdNoValidation")}`);
				return;
			}

			if (validNominees.some(nominee => !nominee.nominee_share || nominee.nominee_share.trim() === '')) {
				showErrorMessage(`${t("messages.nomineeShareValidation")}`);
				return;
			}

			const totalShare = validNominees.reduce((sum, nominee) => sum + Number(nominee.nominee_share), 0);

			if (validNominees.length === 0 && totalShare === 0) {
				navigate(`/get-health-quotes/declaration-two`);
			}

			if (totalShare !== 100) {
				showErrorMessage("Total nominee(s) share must be 100%");
				return;
			}

			setLoading(true);
			const payload = {
				health_id: getOrderDetails.productId,
				nominees: validNominees || []
			};
			
			try {
				const response = await updateHealthEntry(token, payload);
				
				if (response.success) {
					navigate(`/get-health-quotes/declaration-two`);
				} else {
					showErrorMessage(`${t("common.error")}:` + response.message);
					return;
				}
			} catch (error) {
				showErrorMessage(`${t("messages.errorWhileSubmittingDetails")}`);
				return;
			} finally {
				setLoading(false);
			}
		}
	};

	return (
		<div className="content px-2">
			<div className="get-quote-process-container justify-content-between align-content-between flex-row vw-50">
				<div className="d-flex justify-content-between">
					<div className="d-flex">
						<h3 className="m-0">{`${t("healthInsurance.nomination.title")}`}</h3>
						<div className="d-flex align-items-center w-100 mt-2">
							<Image className="mr-2" src={lightbulb} height={36} width={36} />
							<span className="formatted-text text-justify w-85">
								{`${t("healthInsurance.nomination.subTitle")}`}
							</span>
						</div>
					</div>

					{/* Nominee Forms */}
					{nominees.map((nominee, index) => (
						<div key={index} className="nominee-section mt-2">
							<form className="form-input-get-quote w-100">
								<div className="form-input-get-quote-group">
									<div className="row justify-content-between align-items-center mx-0">
										<span className="color-heading fw-bold">{`${t("healthInsurance.nomination.nominate")} ${
											index + 1
										}`}</span>

										{nominees.length > 1 && (
											<Button
												icon="pi pi-minus"
												label={`${t("healthInsurance.nomination.remove")}`}
												className="btn-link-danger-no-fill fw-bold"
												onClick={() => removeNominee(index)}
											/>
										)}
									</div>

									<div className="input-group">
										<FloatLabelInput
											label={`${t("healthInsurance.nomination.nominateName")}`}
											value={nominee.nominee_name}
											onChange={(e) =>
												handleInputChange(index, "nominee_name", e.target.value)
											}
											placeholder=""
										/>
									</div>

									<div className="input-group">
										<FloatLabelInput
											label={`${t("common.identificationNumber")}`}
											value={nominee.nominee_ic}
											onChange={(e) =>
												handleInputChange(index, "nominee_ic", e.target.value)
											}
											placeholder=""
										/>
									</div>

									<div className="row justify-content-between mx-0">
										<div className="col-lg-6 col-sm-6 input-group">
										<FloatDateInput
											label={`${t("common.dateOfBirth")}`}
											value={nominee.nominee_dob ? new Date(nominee.nominee_dob) : null} // Pass a valid Date object or null
											onChange={(e) => handleInputChange(index, "nominee_dob", e.value)} // e.value must return a valid Date object
											dateFormat="yy-mm-dd" // Ensure this matches your backend requirements
											showIcon
										/>
										</div>

										<div className="col-lg-6 col-sm-6 input-group">
											<FloatLabelInput
												label={`${t("common.age")}`}
												value={nominee.nominee_age}
												onChange={(e) =>
													handleInputChange(index, "nominee_age", e.target.value)
												}
												placeholder=""
											/>
										</div>
									</div>

									<div className="input-group">
										<FloatingSelect
											label={`${t("healthInsurance.nomination.relationship")}`}
											options={relationOptions}
											value={nominee.relationship}
											onChange={(e) =>
												handleInputChange(index, "relationship", e.target.value)
											}
										/>
									</div>

									<div className="input-group">
										<FloatingSelect
											label={`${t("healthInsurance.nomination.share")}`}
											options={shareOptions}
											value={nominee.nominee_share}
											onChange={(e) =>
												handleInputChange(index, "nominee_share", e.target.value)
											}
										/>
									</div>
								</div>
							</form>
						</div>
					))}

				{nominees.length < maxNominees && (
					<Button
						className="btn-secondary-fill w-100 add-nominate-button"
						onClick={addNominee}
					>
						<div className="icon-square">
								<i className="pi pi-plus"></i>
							</div>
						<span className="add-nominate-text">{`${t("healthInsurance.nomination.addNominate")}`}</span>
					</Button>
				)}

					{/* {nominees.length < maxNominees && (
						<Button
							className="add-nominate-button"
							onClick={addNominee}
						>
							<div className="icon-square">
								<i className="pi pi-plus"></i>
							</div>
							<span className="add-nominate-text">Add Nominate</span>
						</Button>
					)} */}
				</div>
					<Button
						label={loading ? `${t("common.loading")}...` : `${t("common.proceed")} (5/7)`}
						loading={loading}
						className="btn-primary"
						size="large"
						onClick={(e) => handleButtonClick(e)}
						disabled={loading}
					/>
					{/* BottomMessagePopUp Component */}
					<BottomMessagePopUp
						message={errorMessage}
						type="error"
						isVisible={isErrorVisible}
						onClose={() => setIsErrorVisible(false)}
					/>
			</div>
		</div>
	);
};

export default Nomination;